if(!customElements.get('wlsn-comparison-chart')) {
  class WLSNComparisonChart extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        group: 'js-comparison-group',
        item: 'js-comparison-item',
        slider: 'js-comparison-slider',
        nextButton: 'js-wlsn-slider-next',
        prevButton: 'js-wlsn-slider-prev',
        navigation: 'js-slider-navigation',
        hidden: 'hidden',
        highlighted: 'js-highlighted-item',
      };

      this.alignHeights = this.alignHeights.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this._resizeObserver = null;
      this.swiperInstance = null;
    }

    get elements() {
      return {
        groups: this.querySelectorAll(`.${this.classes.group}`),
        items: this.querySelectorAll(`.${this.classes.item}`),
        slider: this.querySelector(`.${this.classes.slider}`),
        nextButtons: this.querySelectorAll(`.${this.classes.nextButton}`),
        prevButtons: this.querySelectorAll(`.${this.classes.prevButton}`),
        navigations: this.querySelectorAll(`.${this.classes.navigation}`),
      }
    }
    
    connectedCallback() {
      this.initSlider();
      this.alignHeights();

      this._resizeObserver = new ResizeObserver(() => {
        this.alignHeights();
      });
      this.elements.groups.forEach((group) => {
        this._resizeObserver.observe(group);
      });

      window.addEventListener('resize', this.handleResize);
    }
    
    disconnectedCallback() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      window.removeEventListener('resize', this.handleResize);

      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }

    initSlider() {
      const { slider } = this.elements;
      if (!slider) return;

      const highlightedItems = slider.querySelectorAll(`.${this.classes.highlighted}`);
      const slides = Array.from(slider.querySelectorAll(`.${this.classes.group}`));
      const firstHighlighted = highlightedItems[0];
      const initialSlide = firstHighlighted
        ? Math.max(0, slides.findIndex((slide) => slide.contains(firstHighlighted)))
        : 0;

      this.swiperInstance = new Swiper(slider, {
        slidesPerView: 1.1,
        spaceBetween: 12,
        initialSlide: initialSlide,
        loop: false,
        navigation: {
          nextEl: [...this.elements.nextButtons],
          prevEl: [...this.elements.prevButtons],
        },
        observer: true,
        observeParents: true,
        on: {
          init: () => this.updateNavigationVisibility(),
          resize: () => this.updateNavigationVisibility(),
          breakpoint: () => this.updateNavigationVisibility(),
          lock: () => this.updateNavigationVisibility(),
          unlock: () => this.updateNavigationVisibility(),
        },
        breakpoints: {
          768: {
            slidesPerView: 1.8,
            spaceBetween: 12,
          },
        },
      });
      this.updateNavigationVisibility();
    }

    updateNavigationVisibility() {
      const { navigations } = this.elements;
      if (!this.swiperInstance || this.swiperInstance.isLocked) {
        navigations.forEach((nav) => nav.classList.add(this.classes.hidden));
      } else {
        navigations.forEach((nav) => nav.classList.remove(this.classes.hidden));
      }
    }

    handleResize() {
      this.alignHeights();

      if (!this.swiperInstance) return;
      this.swiperInstance.update();
      this.updateNavigationVisibility();
    }

    /**
     * Groups items by data-index across all js-comparison-groups,
     * then sets each index group to the same height (max height in that index).
     */
    alignHeights() {
      const { groups, items } = this.elements;
      if (!groups.length || !items.length) return;

      const byIndex = new Map();
      items.forEach((el) => {
        const index = el.getAttribute('data-index');
        if (index == null) return;
        if (!byIndex.has(index)) byIndex.set(index, []);
        byIndex.get(index).push(el);
      });

      // Reset all heights so we measure natural height
      items.forEach((el) => { el.style.height = ''; });
      // Read natural height per item (before setting any)
      const maxByIndex = new Map();
      byIndex.forEach((itemEls, index) => {
        const maxHeight = Math.max(...itemEls.map((el) => el.offsetHeight));
        maxByIndex.set(index, maxHeight);
      });
      // Apply same height to all items with the same data-index
      byIndex.forEach((itemEls, index) => {
        const height = maxByIndex.get(index);
        itemEls.forEach((el) => {
          el.style.height = `${height}px`;
        });
      });
    }
  }
  customElements.define('wlsn-comparison-chart', WLSNComparisonChart);
}
